import { Card } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';

import {
  ALLERGIES,
  IMMUNIZATIONS,
  MEDICAL_CONDITIONS,
  MEDICATIONS,
  SURGERIES,
} from '@constants/routes';

import type { AllergyRecord } from '@models/allergy';
import type { ImmunizationRecord } from '@models/immunization';
import type { LabResultObservationRecord } from '@models/lab-result';
import type { MedicalConditionRecord } from '@models/medical-condition';
import type { MedicationStatementRecord } from '@models/medication';
import type { ProcedureRecord } from '@models/procedure';
import type { VitalSignObservationRecord } from '@models/vital-sign';

import { filterMedications, filterImmunizations } from '@utils/filter/records';
import {
  sortAllergies,
  sortImmunizations,
  sortMedicalConditions,
  sortMedications,
  sortSurgeries,
} from '@utils/sort/records';

import { SectionH2 } from '../HealthSummary.style';
import ExtendedSection from './ExtendedSection';
import SummarySection from './SummarySection';

const messages = defineMessages({
  summary: {
    id: 'healthSummary.summary',
    defaultMessage: 'medical',
  },
  allergies: {
    id: 'healthSummary.allergies',
    defaultMessage: 'Allergies',
  },
  medications: {
    id: 'healthSummary.medications',
    defaultMessage: 'Medications',
  },
  immunizations: {
    id: 'healthSummary.immunizations',
    defaultMessage: 'Immunizations',
  },
  medicalConditions: {
    id: 'healthSummary.medicalConditions',
    defaultMessage: 'Medical Conditions',
  },
  surgeries: {
    id: 'healthSummary.surgeries',
    defaultMessage: 'Surgeries',
  },
});

interface SummaryProps {
  allergy?: AllergyRecord[];
  condition?: MedicalConditionRecord[];
  immunization?: ImmunizationRecord[];
  medication_statement?: MedicationStatementRecord[];
  procedure?: ProcedureRecord[];
  height_body_observation?: VitalSignObservationRecord | null;
  weight_body_observation?: VitalSignObservationRecord | null;
  blood_type?: LabResultObservationRecord | null;
  clickable?: boolean;
  loading?: boolean;
}

function Summary({
  clickable = true,
  loading = false,
  allergy,
  condition,
  immunization,
  medication_statement,
  procedure,
  blood_type,
  height_body_observation,
  weight_body_observation,
}: SummaryProps) {
  const { formatMessage } = useIntl();

  const allergyData = (allergy ?? []).sort(sortAllergies);
  const procedureData = (procedure ?? []).sort(sortSurgeries);
  const immunizationData = (immunization ?? [])
    .filter(filterImmunizations)
    .sort(sortImmunizations);
  const conditionData = (condition ?? []).sort(sortMedicalConditions);
  const medicationData = (medication_statement ?? [])
    .filter(filterMedications)
    .sort(sortMedications);

  return (
    <Card sx={{ p: 2.5 }}>
      <ContentLoader loading={loading}>
        <>
          <SectionH2>{formatMessage(messages.summary)}</SectionH2>

          <ExtendedSection
            bloodType={blood_type}
            height={height_body_observation ?? undefined}
            weight={weight_body_observation ?? undefined}
          />

          <SummarySection
            addStatus
            clickable={clickable}
            data={allergyData}
            route={ALLERGIES}
            title={formatMessage(messages.allergies)}
            // titleKey="allergy-substance"
          />

          <SummarySection
            clickable={clickable}
            data={conditionData}
            route={MEDICAL_CONDITIONS}
            title={formatMessage(messages.medicalConditions)}
            // titleKey="condition"
          />

          <SummarySection
            clickable={clickable}
            data={procedureData}
            route={SURGERIES}
            title={formatMessage(messages.surgeries)}
            // titleKey="procedure-name"
          />

          <SummarySection
            clickable={clickable}
            data={medicationData}
            route={MEDICATIONS}
            title={formatMessage(messages.medications)}
            // titleKey="medication"
          />

          <SummarySection
            clickable={false}
            countOccurrences
            data={immunizationData}
            // override="immunization-other"
            route={IMMUNIZATIONS}
            title={formatMessage(messages.immunizations)}
            // titleKey="immunization-name"
            // useOption
          />
        </>
      </ContentLoader>
    </Card>
  );
}

export default Summary;
